import { useState, type FormEvent } from 'react';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router';

import { errorMessage } from '../api/client';
import logo from '../assets/brand/maklerpass-logo.svg';
import { FullPageLoader } from '../components/Loader';
import { useAuth } from './context';

export function LoginPage() {
  const { status, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  // Nach „Zugang einrichten“ aus der Einladung: Hinweis und E-Mail vorbelegt.
  const [params] = useSearchParams();
  const invited = params.get('eingeladen') === '1';
  const [email, setEmail] = useState(params.get('email') ?? '');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  if (status === 'loading') return <FullPageLoader label="Anmeldung wird geprüft …" />;
  if (status === 'authenticated' && !submitting) return <Navigate to={from} replace />;

  async function onSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || !password) {
      setError('Bitte E-Mail und Passwort eingeben.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await login(email.trim(), password);
      navigate(from, { replace: true });
    } catch (err) {
      setError(errorMessage(err));
      setSubmitting(false);
    }
  }

  return (
    <div className="login-page">
      <form className="login-card" onSubmit={onSubmit} noValidate aria-labelledby="login-title">
        <div className="login-brand">
          <img className="brand-logo" src={logo} alt="Maklerpass – by Dolisa" />
        </div>
        <h1 id="login-title">Anmelden</h1>
        {invited && !error && (
          <div role="status" className="alert alert-success">
            Ihr Passwort ist gespeichert. Melden Sie sich jetzt an.
          </div>
        )}
        {error && (
          <div role="alert" className="alert alert-error">
            {error}
          </div>
        )}
        <label className="field">
          <span>E-Mail</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span>Passwort</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <button type="submit" className="btn btn-primary btn-block" disabled={submitting}>
          {submitting ? 'Anmelden …' : 'Anmelden'}
        </button>
        {/* Pflichthinweis der Google-Maps-Bedingungen (Nutzungsbedingungen der Anwendung, OFFENE_FRAGEN C-6). */}
        <p className="login-legal">
          Diese Anwendung nutzt Funktionen und Inhalte von <span translate="no">Google Maps</span>. Dafür gelten die{' '}
          <a href="https://maps.google.com/help/terms_maps.html" target="_blank" rel="noreferrer">
            Zusatzbedingungen für Google Maps/Google Earth
          </a>{' '}
          und die{' '}
          <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
            Datenschutzerklärung von Google
          </a>
          .
        </p>
      </form>
    </div>
  );
}
